import { ApiError } from '~/api/errors';
import type { AvailableTenant } from '~/store/auth.store';

/**
 * Server error codes the backend emits when an experiment exists but belongs to
 * a different subsidiary the caller can access. A set — not a single literal —
 * so the backend can ship a resource-specific or generic variant without a
 * frontend change. See `docs/cross-subsidiary-experiment-links.md`.
 */
export const WRONG_SUBSIDIARY_ERROR_CODES = new Set([
  'EXPERIMENT_WRONG_SUBSIDIARY',
  'WRONG_SUBSIDIARY',
]);

/** Envelope field (`error.fields`) carrying the owning subsidiary id. */
export const OWNER_SUBSIDIARY_FIELD = 'subsidiaryId';

/**
 * Decides whether a failed experiment fetch is a recoverable "wrong subsidiary"
 * error. Returns the owning tenant only when the backend named a subsidiary the
 * user can access and isn't already in; null otherwise (genuine not-found, any
 * other error, owner not accessible, or owner already active — the last guards
 * against a switch loop).
 */
export function resolveWrongSubsidiary(
  error: unknown,
  accessibleTenants: AvailableTenant[],
  activeTenantId: string | undefined,
): AvailableTenant | null {
  if (!(error instanceof ApiError)) return null;
  if (!error.serverCode || !WRONG_SUBSIDIARY_ERROR_CODES.has(error.serverCode)) return null;

  const ownerId = error.fields?.[OWNER_SUBSIDIARY_FIELD]?.[0];
  if (!ownerId || ownerId === activeTenantId) return null;

  return accessibleTenants.find((tenant) => tenant.id === ownerId) ?? null;
}
